import { Address, City, ClientBusiness, User } from '@app/types';
import cep, { CEP } from 'cep-promise';
import { RefObject, useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import { v4 as uuidv4 } from 'uuid';
import { CliqueiAcheiUser } from '@app/types/cliqueiachei';
import { APIAddressesShared, SharedAddress } from '@app/services/addresses-shared-api';
import { APICliqueiAchei } from '@app/services/cliqueiachei-api';
import { APIUserData, DeliveryAddress } from '@app/services/api-forms';
import { DeepMap, FieldValues, UseFormReset, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { DialogRefAttrs } from '../DialogAlert';
import { FieldError } from 'react-hook-form';

const CEP_SIZE = 8;

export function useCEPSearch(cepValue: string) {
  const [cepInformation, setCepInformation] = useState<CEP | null>(null);
  const [isFetchingCepInformation, setIsFecthingCepInformation] = useState(false);
  const [hasErrorOnSearch, setHasErrorOnSearch] = useState(false);

  const cepSearchTimeout = useRef<number>();
  useEffect(() => {
    window.clearTimeout(cepSearchTimeout.current);

    if (cepValue.trim().length < CEP_SIZE || cepValue.trim().length > CEP_SIZE) {
      setHasErrorOnSearch(false);
      setIsFecthingCepInformation(false);
      return;
    }

    setIsFecthingCepInformation(true);
    cepSearchTimeout.current = window.setTimeout(async () => {
      setHasErrorOnSearch(false);
      try {
        const cepResponse: CEP = await cep(cepValue, { timeout: 1000 });
        setCepInformation(cepResponse);
      } catch (error) {
        console.log(error);
        setHasErrorOnSearch(true);
      } finally {
        setIsFecthingCepInformation(false);
      }
    }, 300);
  }, [cepValue]);

  return { cepInformation, isFetchingCepInformation, hasErrorOnSearch };
}

const userYupSchema = yup.object().shape({
  cellphone: yup
    .string()
    .required('Informe seu whatsapp')
    .min(10, 'Informe um whatsapp válido')
    .max(11, 'Informe um whatsapp válido'),
  name: yup.string().required('Informe seu nome'),
});

const getAddressYupSchema = (shouldIgnoreNumberValidation: boolean) =>
  yup.object().shape({
    cep: yup
      .string()
      .nullable()
      .test('cep', 'CEP inválido', (value) => {
        if (!value || value.length === 0) return true;
        return /^\d{8}$/.test(value);
      }),
    street: yup.string().required('Informe o nome da rua'),
    number: shouldIgnoreNumberValidation ? yup.string() : yup.string().required('Informe o número'),
    complement: yup.string(),
    neighborhood: yup.string().required('Informe o bairro'),
    city: yup.object().required('Selecione uma cidade'),
  });

const getYupSchemaMerged = (shouldIgnoreNumberValidation: boolean) =>
  userYupSchema.concat(getAddressYupSchema(shouldIgnoreNumberValidation));

export function getYupSchemaResolver(
  hiddenAddressInputs: boolean,
  hiddenUserInputs: boolean,
  shouldIgnoreNumberValidation: boolean
) {
  if (!hiddenAddressInputs && !hiddenUserInputs) return getYupSchemaMerged(shouldIgnoreNumberValidation);
  if (hiddenAddressInputs) return userYupSchema;
  return getAddressYupSchema(shouldIgnoreNumberValidation);
}

export type FormDataSchema = {
  cep: string | undefined;
  street: string;
  number: string | undefined;
  complement: string;
  neighborhood: string;
  landmark: string;
  city: City | null | undefined;
  name: string;
  cellphone: string;
};

export type FieldErrors<
  TFieldValues extends FieldValues = FieldValues
  > = DeepMap<TFieldValues, FieldError>;

export function createAddressFromFormData(
  data: FormDataSchema,
  hasErrorOnSearch: boolean,
  previousAddress: Address | null | undefined
) {
  const result: Address = {
    id: previousAddress ? previousAddress.id : uuidv4(),
    street: data.street.trim(),
    neighborhood: data.neighborhood.trim(),
    number: data.number?.trim() ?? '',
    city: data.city as City,
    complement: data.complement.trim(),
  };
  if (data.cep && !hasErrorOnSearch) result.cep = data.cep;
  if (data.landmark) result.landmark = data.landmark.trim();
  return result;
}

export function createUserFromFromData(data: FormDataSchema) {
  const result: User = {
    name: data.name.trim(),
    cellphone: data.cellphone,
  };
  return result;
}

export function useCellphoneSearchByClient(
  cellphone: string,
  isLoggedInClientArea: boolean,
  client: ClientBusiness | null,
  setFormValue: UseFormSetValue<FormDataSchema>,
  getFormValue: UseFormWatch<FormDataSchema>,
  resetForm: UseFormReset<FormDataSchema>,
  dialogRef: RefObject<DialogRefAttrs>
) {
  const [cliqueiAcheiUserFound, setCliqueiAcheiUserFound] = useState<CliqueiAcheiUser | null>(null);
  const [userNotFound, setUserNotFound] = useState(false);
  const [isSearchingByUser, setIsSearchingByUser] = useState(false);
  const [sharedAddressFound, setSharedAddressFound] = useState<SharedAddress | null>(null);

  const lastCellphoneSearched = useRef('');
  useEffect(() => {
    async function searchByData() {
      if (!isLoggedInClientArea || !client || cellphone?.length !== 11 || cellphone === lastCellphoneSearched.current)
        return;

      try {
        setUserNotFound(false);
        setCliqueiAcheiUserFound(null);
        setSharedAddressFound(null);

        setIsSearchingByUser(true);
        lastCellphoneSearched.current = cellphone;

        const { data } = await APICliqueiAchei.fetchUserByWhatsapp(cellphone);
        setCliqueiAcheiUserFound(data);

        clearFieldsExceptCellphone();
        setFormValue('name', data.Nome);

        const sharedAddressResponse = await APIAddressesShared.fetchByDeviceId(data.DeviceId, client.CidadeId);
        const sharedAddress = sharedAddressResponse?.Data;

        if (sharedAddress && sharedAddress.city.Cod_Cidade === client.CidadeId) {
          setSharedAddressFound(sharedAddress);
          fillFieldsWithSharedAddress(sharedAddress);
        } else {
          const userExtraData = await APIUserData.fetch(data.DeviceId);
          const deliveryAddress = userExtraData?.data.address;
          if (deliveryAddress?.city.Cod_Cidade === client.CidadeId) fillFieldsWithDeliveryAddress(deliveryAddress);
        }
      } catch (error: any) {
        console.error(error);
        if (error?.response?.status === 404) {
          setUserNotFound(true);
          clearFieldsExceptCellphone();
        } else {
          dialogRef.current?.alert({ title: 'Aviso', message: 'Ocorreu um erro inesperado. ' + error.message });
        }
      } finally {
        setIsSearchingByUser(false);
      }

      function clearFieldsExceptCellphone() {
        resetForm({ cellphone: cellphone ?? '', city: getFormValue('city') ?? null });
      }

      function fillFieldsWithDeliveryAddress(deliveryAddress: DeliveryAddress | undefined | null) {
        if (!deliveryAddress) return;
        if (deliveryAddress.street) setFormValue('street', deliveryAddress.street);
        if (deliveryAddress.number) setFormValue('number', deliveryAddress.number);
        if (deliveryAddress.complement) setFormValue('complement', deliveryAddress.complement);
        if (deliveryAddress.landmark) setFormValue('landmark', deliveryAddress.landmark);
        if (deliveryAddress.neighborhood) setFormValue('neighborhood', deliveryAddress.neighborhood);
        if (deliveryAddress.city && !getFormValue('city')) setFormValue('city', deliveryAddress.city);
      }

      function fillFieldsWithSharedAddress(sharedAddress: SharedAddress | undefined | null) {
        if (!sharedAddress) return;
        if (sharedAddress.street) setFormValue('street', sharedAddress.street);
        if (sharedAddress.number) setFormValue('number', sharedAddress.number);
        if (sharedAddress.complement) setFormValue('complement', sharedAddress.complement);
        if (sharedAddress.landmark) setFormValue('landmark', sharedAddress.landmark);
        if (sharedAddress.neighborhood) setFormValue('neighborhood', sharedAddress.neighborhood);
        if (sharedAddress.city && !getFormValue('city')) setFormValue('city', sharedAddress.city);
      }
    }

    searchByData();
  }, [cellphone]);

  function resetSearchForCellphoneStates() {
    setCliqueiAcheiUserFound(null);
    setUserNotFound(false);
    setIsSearchingByUser(false);
    setSharedAddressFound(null);
    lastCellphoneSearched.current = '';
  }

  return {
    cliqueiAcheiUserFound,
    userNotFound,
    isSearchingByUser,
    sharedAddressFound,
    resetSearchForCellphoneStates,
  };
}
